<template>
  <div class="w-full px-4 pt-0">
    <div class="group-grid">
      <div v-for="(group, groupLabel) in groupedAttributes" :key="groupLabel" class="group-section">
        <h3 class="group-title">
          <i
            v-if="getGroupIcon(groupLabel, group)"
            :class="['group-icon', getGroupIcon(groupLabel, group)]"
            aria-hidden="true"
          ></i>
          {{ groupLabel }}
        </h3>
        <table class="custom-table">
          <tbody>
            <tr v-for="(attribute, index) in group" :key="index">
              <th class="label-cell">
                {{ attribute.options[selectedLanguage] }}
              </th>
              <td class="value-cell">
                <template v-if="attribute.type === 'pim_catalog_multiselect'">
                  <div class="chip-container">
                    <!-- {{ attribute }} -->
                    <template v-for="(option, i) in attribute.data" :key="i">
                      <ProductInfoPegiDescriptorBadge
                        v-if="isPegiDescriptors(attribute)"
                        :option="option"
                        :language="selectedLanguage"
                        img-class="h-16 w-auto"
                        chip-class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded"
                      />
                      <span
                        v-else
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded"
                      >
                        {{ option.Labels[selectedLanguage] || option }}
                      </span>
                    </template>
                  </div>
                </template>
                <template v-else>
                  <ClientOnly>
                    <ProductInfoRatingBadge v-if="/pegi|usk|esrb/i.test(attribute.options?.en_GB || '')"
                      :label="attribute.options?.en_GB" :value="formatValue(attribute)" img-class="h-16 w-auto" />
                    <template v-else>{{ formatValue(attribute) }}</template>
                  </ClientOnly>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>


const props = defineProps({
  attributesJson: {
    type: Object,
    required: true
  },
  selectedLanguage: {
    type: String,
    default: 'en_GB'
  }
})

// Group attributes by their en_GB group label
const groupedAttributes = computed(() => {
  const groups = {}
  Object.values(props.attributesJson).flat().forEach(attr => {
    const label = attr.groupLables[props.selectedLanguage] || attr.groupLables['fr_BE'] || attr.groupLables['en_GB'] || 'Other';
    if (!groups[label]) groups[label] = []
    groups[label].push(attr)
  })
  return groups
})


// PEGI content descriptors render as official pictograms instead of text chips.
const isPegiDescriptors = (attr) =>
  attr?.data?.[0]?.Attribute === 'vg_pegi_descriptors' || /pegi/i.test(attr?.options?.en_GB || '');

// Format value types
const formatValue = (attr) => {
  if (attr.type === 'pim_catalog_boolean') {
    return attr.data ? 'Yes' : 'No'
  } else if (attr.type === 'pim_catalog_date') {
    return attr.data?.split('T')[0] || '—'
  } else if (typeof attr.data === 'object') {
    return attr.data[props.selectedLanguage] || attr.data?.fr_BE || '—'
  }
  return attr.data != undefined ? attr.data : attr
}

// Icon mapping per group title
const groupIconMap = {
  'Main Data': 'fa fa-database',
  'VAT': 'fa fa-file-text-o',
  'Dates': 'fa fa-calendar',
  'Languages': 'fa fa-language',
  'Pricing': 'fa fa-eur',
  'Options': 'fa fa-cogs',
  'Features': 'fa fa-star-o',
  'Box Content': 'fa fa-archive',
  'Certifications & Customs Information': 'fa fa-certificate',
  'Weight, dimensions & sizes': 'fa fa-arrows-alt',
  'Virtual products': 'fa fa-cloud',
  'Other': 'fa fa-info-circle',
  'Connectivity': 'fa fa-plug',
  'Rating': 'fa fa-star-half-o',
  'Materials': 'fa fa-cubes',
  'Lighting': 'fa fa-lightbulb-o',
  'Battery': 'fa fa-battery-half',
  'Safety warning': 'fa fa-exclamation-triangle',
  'Publishing': 'fa fa-newspaper-o',
  'Audio': 'fa fa-volume-up',
}

const getGroupIcon = (groupLabel, group) => {
  if (groupLabel && groupIconMap[groupLabel]) {
    return groupIconMap[groupLabel]
  }
  const englishGroupLabel = group?.[0]?.groupLables?.en_GB
  if (englishGroupLabel && groupIconMap[englishGroupLabel]) {
    return groupIconMap[englishGroupLabel]
  }
  return ''
}

</script>

<style scoped>
.group-grid {
  column-count: 2;
  column-gap: 16px;
  padding-top: 8px;
}

@media (min-width: 1024px) {
  .group-grid {
    column-count: 3;
  }
}

@media (min-width: 1440px) {
  .group-grid {
    column-count: 4;
  }
}

.group-section {
  display: inline-block;
  width: 100%;
  break-inside: avoid;
  background: #fff;
  border-radius: 12px;
  padding-right: 1px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.custom-table th,
.custom-table td {
  border-bottom: 1px solid #e0e0e0;
  padding: 10px 12px;
  text-align: left;
  vertical-align: top;
}

.label-cell {
  font-weight: 600;
  background: #f4f4f4;
  width: 35%;
  border-right: 1px solid #e0e0e0;

}

.value-cell {
  background-color: #fcfcfc;
}

.group-title {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
  border-left: 4px solid var(--color-primary);
  padding-left: 10px;
  display: flex;
  align-items: center;
}

.group-icon {
  margin-right: 8px;
  color: var(--color-primary);
}

.group-icon:hover {
  cursor: pointer;
  transition: 0.1s ease;
  transform: scale(1.2);
}

.chip-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pegi-icon {
  height: 75px;
  filter: grayscale(100%);
  transition: filter 0.5s ease;
  border-radius: 1%;

}

.pegi-icon:hover {
  cursor: pointer;
  filter: grayscale(0%);
}

/* Mobile / small-screen adjustments */
@media (max-width: 767px) {
  .group-grid {
    column-count: 1;
    column-gap: 12px;
    padding-top: 6px;
  }

  .group-section {
    padding: 12px;
    margin-bottom: 12px;
    border-radius: 10px;
  }

  .group-title {
    font-size: 15px;
    padding-left: 8px;
  }

  /* Make table rows stack for better mobile readability */
  .custom-table {
    display: block;
    width: 100%;
  }

  .custom-table tbody {
    display: block;
  }

  .custom-table tr {
    display: block;
    margin-bottom: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
  }

  .custom-table th,
  .custom-table td {
    display: block;
    width: 100%;
    padding: 6px 0;
    border-bottom: none;
    text-align: left;
  }

  .label-cell {
    background: transparent;
    font-weight: 700;
    width: 100%;
    padding-right: 0;
    margin-bottom: 4px;
    color: #333;
  }

  .value-cell {
    background-color: transparent;
    padding-bottom: 6px;
  }

  .pegi-icon {
    height: 56px;
  }

  .chip-container {
    gap: 4px;
  }
}

/* Extra small phones */
@media (max-width: 420px) {
  .group-title {
    font-size: 14px;
  }

  .pegi-icon {
    height: 48px;
  }
}

/* Dark theme: the attribute cards/tables hardcode light colours above. */
html.dark .group-section { background: var(--cld-dark-surface); }
html.dark .group-title { color: var(--cld-dark-text); }
html.dark .custom-table th,
html.dark .custom-table td { border-bottom-color: var(--cld-dark-border); }
html.dark .custom-table tr { border-bottom-color: var(--cld-dark-border); }
html.dark .label-cell {
  background: var(--cld-dark-surface-2);
  border-right-color: var(--cld-dark-border);
  color: var(--cld-dark-text);
}
html.dark .value-cell {
  background-color: var(--cld-dark-surface);
  color: var(--cld-dark-text);
}
</style>
